import {Component, EventEmitter, inject, OnInit} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";

import {ToastrService} from "ngx-toastr";
import {NgSelectComponent} from "@ng-select/ng-select";
import {
  NgbActiveModal,
  NgbCalendar,
  NgbDateParserFormatter,
  NgbDatepickerI18n,
  NgbDateStruct,
  NgbInputDatepicker
} from "@ng-bootstrap/ng-bootstrap";

import {environment} from "@numizmat/environments";
import {ImageComponent} from "@numizmat/components";
import {AdApiService} from "@numizmat/services/core";
import {HOURS_OPTIONS, MINUTES_OPTIONS} from "@numizmat/constants";
import {AdRenewableResponse, RenewalAdsRequest} from "@numizmat/services-models/core";
import {CustomDateParserFormatter, CustomDatepickerI18n, DatepickerService, I18n} from "@numizmat/services/common";

@Component({
  selector: 'nz-ad-renewal-batch-modal',
  standalone: true,
  imports: [
    FormsModule,
    NgSelectComponent,
    NgbInputDatepicker,
    ReactiveFormsModule,
    ImageComponent
  ],
  templateUrl: './ad-renewal-batch-modal.component.html',
  styleUrl: './ad-renewal-batch-modal.component.scss',
  providers: [I18n,
    {provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n},
    {provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter}
  ]
})
export class AdRenewalBatchModalComponent implements OnInit {
  private calendar: NgbCalendar = inject(NgbCalendar);
  private adApiService: AdApiService = inject(AdApiService);
  private activeModal: NgbActiveModal = inject(NgbActiveModal);
  private toastrService: ToastrService = inject(ToastrService);
  private datepickerService: DatepickerService = inject(DatepickerService);

  protected loadingData: boolean = true;
  protected loadingSubmit: boolean = false;
  protected formInvalidError: boolean = false;
  protected notSelectedAdsError: boolean = false;
  protected endDateMinDate!: NgbDateStruct;
  protected endDateMaxDate!: NgbDateStruct;
  protected selectedAdIds: string[] = [];
  protected form: FormGroup = this.initializeForm();
  protected reloadData: EventEmitter<boolean> = new EventEmitter();
  protected renewableAds: AdRenewableResponse[] = [];

  protected readonly environment = environment;
  protected readonly hourOptions = HOURS_OPTIONS;
  protected readonly minutesOptions = MINUTES_OPTIONS;

  public ngOnInit(): void {
    this.getAllAdsForRenewal();
    this.initializeMinAndMaxDate();
    const date = new Date();
    this.endHour?.setValue(date.getHours());
    this.endMinutes?.setValue(date.getMinutes());
    this.endDate?.setValue(this.calendar.getNext(this.calendar.getToday(), 'd', 10));
  }

  protected onSubmit(): void {
    this.form.markAllAsTouched();
    this.formInvalidError = this.form.invalid;
    this.notSelectedAdsError = this.selectedAdIds.length === 0;
    if (this.formInvalidError || this.notSelectedAdsError) {
      return;
    }
    this.loadingSubmit = true;
    const formValue = this.form.getRawValue();
    const endDateTime = this.datepickerService.parseNgbDateStructToIsoFormat(formValue.endDate, formValue.endHour, formValue.endMinutes);
    const request: RenewalAdsRequest = {
      adIds: this.selectedAdIds,
      endDateTime: endDateTime
    };
    this.adApiService.renewalAdsAfterExpired(request).subscribe({
      next: response => {
        this.loadingSubmit = false;
        if (response) {
          this.reloadData.emit(true);
          this.toastrService.success('Успешно обновяване.');
        } else {
          this.toastrService.error('Възникна грешка.');
        }
        this.closeModal();
      }
    });
  }

  protected closeModal(): void {
    this.activeModal.dismiss();
  }

  protected toggleAdSelection(adId: string): void {
    const index: number = this.selectedAdIds.findIndex(x => x === adId);
    if (index >= 0) {
      this.selectedAdIds.splice(index, 1);
    } else {
      this.selectedAdIds.push(adId);
    }
  }

  protected toggleSelectAll(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      this.selectedAdIds = this.renewableAds.map(x => {
        return x.adId
      });
    } else {
      this.selectedAdIds = [];
    }
  }

  protected areAllSelected(): boolean {
    return this.renewableAds.length !== 0 && this.selectedAdIds.length === this.renewableAds.length;
  }

  private initializeForm() {
    return new FormGroup({
      endDate: new FormControl(null, [Validators.required]),
      endHour: new FormControl(null, [Validators.required]),
      endMinutes: new FormControl(null, [Validators.required])
    });
  }

  private getAllAdsForRenewal(): void {
    this.adApiService.getAllRenewableAds().subscribe({
      next: response => {
        this.loadingData = false;
        this.renewableAds = response;
      }
    })
  }

  private initializeMinAndMaxDate() {
    this.endDateMinDate = this.calendar.getToday();
    this.endDateMaxDate = this.calendar.getNext(this.calendar.getToday(), 'm', 6);
  }

  get endDate() {
    return this.form.get('endDate');
  }

  get endHour() {
    return this.form.get('endHour');
  }

  get endMinutes() {
    return this.form.get('endMinutes');
  }
}
